import { useFormContext, useWatch, FormState } from 'react-hook-form';

import { supportedMediums } from 'modules/frequencyManagement/lib/utils/mediumDomainUtils';

import {
  FrequencyManagementSchema,
  MESSAGE_TYPES,
  MessageTypeSchema,
  MODE,
} from 'modules/frequencyManagement/contexts/FormProvider';
import { FrequencyManagementContext } from 'modules/frequencyManagement/types';
import { FrequencyMode, SupportedMediums } from 'modules/frequencyManagement/lib/types';
import { isNonEmptyArray } from 'utils/tsHelpers';
import { useCoreContext } from 'modules/frequencyManagement/contexts/CoreContext';
import { OptimizeModeLimit } from 'modules/frequencyManagement/lib/constants';

export const getUntrackedOptimizeCount = (
  trackedMessageMedium: SupportedMediums,
  getOptimizeModeCountByMedium: FrequencyManagementContext['getOptimizeModeCountByMedium'],
) => {
  const untrackedMediums = supportedMediums.filter((medium) => medium !== trackedMessageMedium);

  // Get the count of optimizations for each saved medium
  return untrackedMediums.reduce((accCount, currentMedium) => {
    return accCount + getOptimizeModeCountByMedium(currentMedium);
  }, 0);
};

export const modifyOptimizeCountByFormMode = (fieldMode: FrequencyMode, optimizeCount: number) => {
  if (fieldMode === FrequencyMode.Optimize) {
    return optimizeCount + 1;
  }
  return optimizeCount;
};

export const getMessageTypeOptimizeCount = (
  savedMessageTypes: MessageTypeSchema[],
  formMessageTypes: MessageTypeSchema[],
  dirtyMessageTypes: FormState<MessageTypeSchema[]>['dirtyFields'] | undefined,
  trackedOptimizeCount: number,
) => {
  let currentOptimizeCount = trackedOptimizeCount;
  if (isNonEmptyArray(savedMessageTypes)) {
    const hasDirtyMessageTypes = isNonEmptyArray(dirtyMessageTypes);
    // Index approach works if the message types are tracked sorted within the form provider
    for (let index = 0; index < savedMessageTypes.length; index++) {
      const isMessageTypeDirty = hasDirtyMessageTypes
        ? dirtyMessageTypes[index] !== undefined
        : false;
      const savedMessageTypeMode = (savedMessageTypes[index] as MessageTypeSchema)[MODE];
      const trackedMessageTypeMode = isMessageTypeDirty
        ? formMessageTypes[index].mode
        : FrequencyMode.Off;

      currentOptimizeCount = modifyOptimizeCountByFormMode(
        isMessageTypeDirty ? trackedMessageTypeMode : savedMessageTypeMode,
        currentOptimizeCount,
      );
    }
  }
  return currentOptimizeCount;
};

export const useOptimizeModeTotal = (messageMedium: SupportedMediums) => {
  const { getOptimizeModeCountByMedium } = useCoreContext();
  const {
    formState: { defaultValues, dirtyFields },
  } = useFormContext<FrequencyManagementSchema>();
  const messageMediumMode = useWatch({ name: MODE });
  const messageTypes = useWatch({ name: MESSAGE_TYPES });

  const untrackedOptimizeCount = getUntrackedOptimizeCount(
    messageMedium,
    getOptimizeModeCountByMedium,
  );

  const savedMode = (defaultValues as FrequencyManagementSchema)[MODE];
  const { mode: modeIsDirty, messageTypes: dirtyMessageTypes } = dirtyFields;

  // Check message medium mode
  let trackedOptimizeCount = modifyOptimizeCountByFormMode(
    modeIsDirty ? messageMediumMode : savedMode,
    0,
  );

  // Check message types
  trackedOptimizeCount = getMessageTypeOptimizeCount(
    (defaultValues as FrequencyManagementSchema)[MESSAGE_TYPES] as MessageTypeSchema[],
    messageTypes,
    dirtyMessageTypes,
    trackedOptimizeCount,
  );

  const remainingCount = OptimizeModeLimit - (trackedOptimizeCount + untrackedOptimizeCount);

  return {
    isAtOptimizeLimit: remainingCount === 0,
    remainingOptimizeCount: remainingCount,
    totalOptimizeCount: trackedOptimizeCount + untrackedOptimizeCount,
  };
};
